import { ChangeEvent, Study, StudyEntityType, StudyWithChildren } from '@faro/study-designer-model';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { UseQueryConsumerOptions } from '../../package/react-query';
import { RealtimeEntityDataHookFactory } from '../../package/realtime-query';
import { useServiceContext } from '../ServiceContext';
import { SearchStudyPaginatedRequest, studyClient, StudyFindAllQuery } from './StudyClient';

export function useStudySearchTree(
    query?: SearchStudyPaginatedRequest,
    options?: UseQueryConsumerOptions<StudyWithChildren[]>
): UseQueryResult<StudyWithChildren[]> {
    return useQuery({
        ...options,
        queryKey: ['studyTree', query],
        queryFn: () => studyClient.searchTree(query),
    });
}

export function useStudyList(
    query?: StudyFindAllQuery,
    options?: UseQueryConsumerOptions<Study[]>
): UseQueryResult<Study[]> {
    return useQuery({
        ...options,
        queryKey: ['studyList', query],
        queryFn: () => studyClient.list(query),
    });
}

// Custom scope parameters for Study entity - only needs id since studyId = id for Study entity
export type StudyScopeParameters = { id: string; revision?: number };

// Custom factory for Study entity that maps id to studyId
function createStudyHookFactory() {
    return new RealtimeEntityDataHookFactory<ChangeEvent, StudyScopeParameters, Study>({
        scopeName: 'study',
        getScopeId: (params: StudyScopeParameters) => params.id, // Map id to studyId for scope
        getChangeEventService: () => {
            const { changeEventService } = useServiceContext();
            return changeEventService;
        },
        type: StudyEntityType.Study,
    });
}

const factory = createStudyHookFactory();

export const useStudy = factory.get(studyClient.get);
